import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";

export type GiftCardResponseData = {
  original_amount_int: number;
  available_amount_int: number;
  expiry_date: string;
  status: string;
  body?: string;
};

export type GiftCardErrorResponse = {
  status: "error";
  body: string;
};

export type APIGiftCardResponse = GiftCardResponseData | GiftCardErrorResponse;

interface GiftCardDetails {
  originalAmount: number;
  availableAmount: number;
  expiryDate: string;
}

// Create a portal root if it doesn't exist
let portalRoot = document.getElementById("giftcard-modal-portal-root");
if (!portalRoot) {
  portalRoot = document.createElement("div");
  portalRoot.setAttribute("id", "giftcard-modal-portal-root");
  document.body.appendChild(portalRoot);
}

interface ModalProps {
  children: React.ReactNode;
  onClose: () => void;
  isLoading?: boolean;
  translations: any;
}

const Modal: React.FC<ModalProps> = ({ children, onClose: parentOnClose, isLoading, translations }) => {
  const [animationClass, setAnimationClass] = useState("opacity-0 translate-y-10");

  useEffect(() => {
    requestAnimationFrame(() => {
      setAnimationClass("opacity-100 translate-y-0");
    });

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isLoading) {
        initiateClose();
      }
    };
    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isLoading]);

  const initiateClose = () => {
    if (isLoading) return;
    setAnimationClass("opacity-0 translate-y-10");
    setTimeout(() => {
      parentOnClose();
    }, 300);
  };

  if (!portalRoot) return null;

  return createPortal(
    <div
      className="fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center z-50"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      onClick={initiateClose}
    >
      <div
        className={`bg-brand-white px-6 pt-12 pb-8 rounded-lg shadow-xl w-11/12 max-w-md relative
                    transition-all duration-300 ease-in-out transform ${animationClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-4 right-4">
          <button
            type="button"
            onClick={initiateClose}
            disabled={isLoading}
            className="header__icon header__icon--account link w-auto h-auto"
            aria-label={translations?.close_button_aria_label || "Close modal"}
          >
            <ui-icon width="16" height="16" name="icon-close-large"></ui-icon>
          </button>
        </div>
        {children}
      </div>
    </div>,
    portalRoot
  );
};

interface GiftCardBalanceCheckerComponentProps {
  translations: any;
  className?: string;
}

const GiftCardBalanceCheckerComponent: React.FC<GiftCardBalanceCheckerComponentProps> = ({
  translations,
  className,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [voucherNumber, setVoucherNumber] = useState("");
  const [pin, setPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [giftCardDetails, setGiftCardDetails] = useState<GiftCardDetails | null>(null);

  const openModal = () => {
    setIsModalOpen(true);
    setError(null);
    setGiftCardDetails(null);
    setVoucherNumber("");
    setPin("");
  };
  const closeModal = () => setIsModalOpen(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setGiftCardDetails(null);

    if (!voucherNumber || !pin) {
      setError(translations?.errors?.required_fields || "Voucher number and PIN are required.");
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("number", voucherNumber);
    formData.append("pin", pin);

    try {
      const response = await fetch("/api/check-gift-balance", {
        method: "POST",
        body: formData,
      });

      const result = (await response.json()) as APIGiftCardResponse;

      if (!response.ok || result.status === "error") {
        const errorMessage =
          (result as GiftCardErrorResponse)?.body ||
          translations?.errors?.failed_to_fetch ||
          "Failed to check gift card balance.";
        setError(errorMessage);
        setGiftCardDetails(null);
      } else if (result.status === "success" || (result as GiftCardResponseData)?.original_amount_int !== undefined) {
        const data = result as GiftCardResponseData;
        setGiftCardDetails({
          originalAmount: data.original_amount_int,
          availableAmount: data.available_amount_int,
          expiryDate: data.expiry_date,
        });
        setError(null);
      } else {
        setError(result.body || translations?.errors?.unexpected || "An unexpected error occurred.");
        setGiftCardDetails(null);
      }
    } catch (err) {
      console.error("Error checking gift card balance:", err);
      setError(translations?.errors?.general || "An error occurred while checking the balance. Please try again.");
      setGiftCardDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button type="button" className={`button w-full ${className || ""}`} onClick={openModal}>
        {translations?.open_button_text || "Check Gift Card Balance"}
      </button>

      {isModalOpen && (
        <Modal onClose={closeModal} isLoading={isLoading} translations={translations}>
          <div className="flex justify-between items-center mb-8">
            <h2 className="h2 text-brand-dark-grey font-semibold">
              {translations?.modal_title || "Check Gift Card Balance"}
            </h2>
          </div>
          {giftCardDetails ? (
            <div className="flex flex-col gap-2">
              <div className="flex gap-1 justify-between">
                <p className="mb-0 font-semibold">{translations?.original_amount_text || "Original Amount:"}</p>
                <p className="mb-0">${(giftCardDetails.originalAmount / 100).toFixed(2)}</p>
              </div>
              <div className="flex gap-1 justify-between">
                <p className="mb-0 font-semibold">{translations?.available_amount_text || "Available Amount:"}</p>
                <p className="mb-0">${(giftCardDetails.availableAmount / 100).toFixed(2)}</p>
              </div>
              <div className="flex gap-1 justify-between">
                <p className="mb-0 font-semibold">{translations?.expiry_date_text || "Expiry Date:"} </p>
                <p className="mb-0">{new Date(giftCardDetails.expiryDate).toLocaleDateString()}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-3 _field">
                <input
                  type="text"
                  id="voucherNumber"
                  value={voucherNumber}
                  onChange={(e) => setVoucherNumber(e.target.value)}
                  required
                  className="_field__input"
                />
                <label htmlFor="voucherNumber" className="_field__label">
                  {translations?.voucher_number_label || "Voucher Number:"}
                </label>
              </div>
              <div className="mb-3 _field">
                <input
                  type="password"
                  id="pin"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  required
                  className="_field__input"
                />
                <label htmlFor="pin" className="_field__label">
                  {translations?.pin_label || "PIN:"}
                </label>
              </div>
              {error && (
                <div className="mt-4">
                  <p className="text-red-600 text-sm text-brand-error">{error}</p>
                </div>
              )}
              <div className="mt-6 flex justify-end">
                <button type="submit" disabled={isLoading} className="button w-full">
                  {isLoading
                    ? translations?.checking_button_text || "Checking..."
                    : translations?.check_balance_button_text || "Check Balance"}
                </button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </>
  );
};

class GiftCardBalanceCheckerElement extends HTMLElement {
  private translations: any = {};
  private root: ReturnType<typeof createRoot> | null = null;
  private observer: MutationObserver | null = null;

  connectedCallback() {
    const script = this.querySelector('script[type="application/json"][data-translations]');
    if (script) {
      try {
        this.translations = JSON.parse(script.textContent || "{}");
      } catch (e) {
        console.error("Failed to parse translations for gift card balance checker", e);
        this.translations = {};
      }
    }

    if (document.body.contains(this)) {
      this.renderComponent();
    } else {
      this.observer = new MutationObserver(() => {
        if (document.body.contains(this)) {
          this.renderComponent();
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
        }
      });
      this.observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  private renderComponent() {
    if (this.root) return;

    const mountPoint = document.createElement("div");
    this.appendChild(mountPoint);

    const componentClassName = this.getAttribute("class") || "";

    this.root = createRoot(mountPoint);
    this.root.render(
      <React.StrictMode>
        <GiftCardBalanceCheckerComponent translations={this.translations} className={componentClassName} />
      </React.StrictMode>
    );
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
    const mountPoint = this.querySelector("div");
    if (mountPoint) {
      this.removeChild(mountPoint);
    }
  }
}

if (!customElements.get("gift-card-balance-checker")) {
  customElements.define("gift-card-balance-checker", GiftCardBalanceCheckerElement);
}

export default GiftCardBalanceCheckerElement;
